import { Column, GridReadyEvent } from 'ag-grid-community';
import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FilterChip,
  FilterChipStateService,
} from '@compumark/brand-context-components';
import { Order } from '@compumark/order-management-client';
import { isEmpty } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { GridOptionsService } from '../columns/column-definition/grid-options.service';
import { GridLocalizationService } from '../localization/services/grid-localization.service';
import { OrderSelectionService } from '../selection/order-selection.service';
import { NavigationBarService } from '../services/navigation-bar/navigation-bar.service';
import { OrdersApiClientService } from '../services/orders.api.client.service';
import { PaginationStatusBarsComponent } from '../status-bar/pagination/pagination-status-bars/pagination-status-bars.component';
import { SelectedOrderCountComponent } from '../status-bar/selected-order-count/selected-order-count.component';

@Component({
  selector: 'app-main-grid',
  templateUrl: './main-grid.component.html',
  styleUrls: ['./main-grid.component.scss'],
  standalone: false,
})
export class MainGridComponent implements OnInit, OnDestroy {
  gridLocalizationService = inject(GridLocalizationService);
  orderService = inject(OrdersApiClientService);
  private gridOptionsService = inject(GridOptionsService);
  private navigationBarService = inject(NavigationBarService);
  private route = inject(ActivatedRoute);
  private filterChipStateService = inject(FilterChipStateService);
  private orderSelectionService = inject(OrderSelectionService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public gridOptions: any;
  public localeText: Observable<any>;
  public tableRowData!: Observable<Order[]>;
  private destroy$ = new Subject<boolean>();
  private sideNavOpen = false;

  public statusBar = {
    statusPanels: [
      { statusPanel: SelectedOrderCountComponent, align: 'left' },
      { statusPanel: PaginationStatusBarsComponent },
    ],
  };
  constructor() {
    this.gridOptions = this.gridOptionsService.gridOptions;
    this.localeText = this.gridLocalizationService.getLocaleText();
  }

  ngOnInit(): void {
    this.navigationBarService.sideNavBar$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.sideNavOpen = !!isOpen;
      });
    this.tableRowData = this.route.paramMap.pipe(
      switchMap((params) => {
        this.gridOptions.api?.showLoadingOverlay();
        this.gridOptionsService.updateColumnDefs(
          params.get('status') as 'active' | 'inactive',
        );
        this.gridOptions.api?.paginationGoToFirstPage();
        return this.orderService.getOrders(
          params.get('status') as 'active' | 'inactive',
        );
      }),
    );
    this.handleFilterChipStateChange();
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridOptions.api = event.api;
    this.gridOptionsService.setGridApi(event.api);
    this.gridOptionsService.updateColumnDefs(this.orderService.getActiveTab());
  }

  onRowDataUpdate(): void {
    this.orderSelectionService.markDataLoaded();
    this.gridOptions.api?.forEachNode((node: any) => {
      if (node.data.selected) {
        node.setSelected(node.data.selected);
      }
    });
  }

  isSidePanelOpen(): boolean {
    return this.sideNavOpen;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  handleFilterChipStateChange(): void {
    this.filterChipStateService
      .getfilterChipChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe((filterChips) => {
        this.changeDetectorRef.detectChanges();
        filterChips.forEach((chip) => {
          if (!chip.value) {
            chip.value = chip.valueLabel;
          }
        });
        void this.syncColumnFiltersWithChips(filterChips);
      });
  }

  private async syncColumnFiltersWithChips(
    filterChips: FilterChip[],
  ): Promise<void> {
    const api = this.gridOptions.api;
    if (api?.getFilterModel()) {
      const filterFields = [
        ...new Set(filterChips.map((filterChip) => filterChip.field)),
      ];
      const activeFilterColumns = (api.getColumns() ?? []).filter(
        (col: Column) => col.isFilterActive(),
      );
      const filterData = await Promise.all(
        activeFilterColumns.map(async (col: Column) => ({
          columnId: col.getColId(),
          filter: await api.getColumnFilterInstance(col),
          count: filterChips
            .map((filterChip) => filterChip.field)
            .filter((field: string) => field === col.getColId()).length,
          filterChips,
        })),
      );
      filterData.forEach(
        (data: {
          columnId: string;
          filter: any;
          count: number;
          filterChips: FilterChip[];
        }) => {
          if (!data.filter) {
            return;
          }
          if (!filterFields.includes(data.columnId)) {
            data.filter.setModel(null);
          }
          this.updateTableSetFilterModel(data);
          this.updateCustomTextAndSetFilterModel(data);
        },
      );
    }
    this.notifyGrid();
  }

  updateTableSetFilterModel(data: {
    columnId: string;
    filter: any;
    count: number;
    filterChips: FilterChip[];
  }): void {
    const filterModel = data.filter.getModel();
    if (
      filterModel?.filterType === 'set' &&
      data.count < filterModel?.values?.length
    ) {
      data.filter.setModel({
        ...filterModel,
        values: data.filterChips
          .filter(
            (filterChip) =>
              filterChip.field === data.columnId && filterChip.value,
          )
          .map((filterChip) => filterChip.value),
      });
    }
  }
  updateCustomTextAndSetFilterModel(data: {
    columnId: string;
    filter: any;
    count: number;
    filterChips: FilterChip[];
  }): void {
    const filterModel = data.filter.getModel();

    if (
      !isEmpty(filterModel?.filterType) &&
      filterModel.filterType === 'customSetAndText' &&
      data.count <
        (isEmpty(filterModel.textFilter)
          ? filterModel.selectedValue.values?.length
          : filterModel.selectedValue.values?.length + 1)
    ) {
      const selectedValues: Array<string> = [];
      const updatedTextFilter: Array<string> = [];
      data.filterChips
        .filter((filterChip) => filterChip.field === data.columnId)
        .map((filterChip) => {
          if (filterChip.value) {
            selectedValues.push(filterChip.value);
          }
          if (filterChip.valueLabel) {
            updatedTextFilter.push(filterChip.valueLabel);
          }
        });

      if (updatedTextFilter.length === 0) {
        filterModel.textFilter = {};
      }

      data.filter.setModel({
        ...filterModel,
        selectedValue: {
          values: selectedValues,
        },
      });
    }
  }

  notifyGrid(): void {
    const api = this.gridOptions.api;
    api?.onFilterChanged();
    api?.ensureIndexVisible(0, 'top');
  }
}
